/**
 * Returns the date the subscription became expired, only if the user lost PRO privileges.
 */
import useAuthActiveSubscriptionTiers from '@apps/www/src/www/hooks/useAuthActiveSubscriptionTiers';
import useAuthSubscriptions from '@apps/www/src/www/hooks/useAuthSubscriptions';
import useAuthTeam from '@apps/www/src/www/hooks/useAuthTeam';
import ExpiredMessageType from '@pkgs/shared/enums/ExpiredMessageType';
import SubscriptionStatus from '@pkgs/shared/enums/SubscriptionStatus';
import SubscriptionType from '@pkgs/shared/enums/SubscriptionType';

export default function useAuthSubscriptionIsExpired(): {
	needsRenewSince: Date;
	expiredMessageType: ValueOf<typeof ExpiredMessageType>;
} | null {
	const activeTiers = useAuthActiveSubscriptionTiers();
	const subscriptions = useAuthSubscriptions();
	const team = useAuthTeam();

	// If user has any active subscription, we don't show the expired message
	if (activeTiers.length > 0) {
		return null;
	}

	let expiredMessageType: ValueOf<typeof ExpiredMessageType> = ExpiredMessageType.DEFAULT;

	// First check for an expired TEAM subscription
	const expiredTeamSubscription = subscriptions.find(
		(subscription) =>
			subscription.type === SubscriptionType.TEAM &&
			subscription.status === SubscriptionStatus.EXPIRED,
	);

	if (expiredTeamSubscription && expiredTeamSubscription.needsRenewSince && team) {
		// Special case for PicPay team members, we show a special message in portuguese
		if (expiredTeamSubscription.planID === 'price_1PGb3uFjSezAyjTf5MeaxKML') {
			expiredMessageType = ExpiredMessageType.PICPAY;
		}

		return {
			expiredMessageType,
			needsRenewSince: expiredTeamSubscription.needsRenewSince,
		};
	}

	// Then an expired INDIVIDUAL subscription
	const expiredIndividualSubscription = subscriptions.find(
		(subscription) =>
			subscription.type === SubscriptionType.INDIVIDUAL &&
			subscription.status === SubscriptionStatus.EXPIRED,
	);

	if (expiredIndividualSubscription && expiredIndividualSubscription.needsRenewSince) {
		return {
			expiredMessageType,
			needsRenewSince: expiredIndividualSubscription.needsRenewSince,
		};
	}

	return null;
}
